import { createBrowserRouter, Outlet } from 'react-router-dom';

import AnalyticsProvider from '@/modules/analytics/context/analytics.provider';
import PlaygroundDataLoader from '@/modules/playground/components/PlaygroundDataLoader';
import CheckoutEmbedProvider from '@/modules/subscription/context/checkout-embed.context';
import useKibo from '@/core/hooks/useKibo.hook';
import ConsentConfigurator from '@/core/consent/ConsentConfigurator';

import { removeHttpProtocol } from '@linkie/shared';
import { RootErrorBoundary } from '@/core/error-boundaries/RootErrorBoundary';
import ProtectedRoute from './Protected.route';

const KiboSync = () => {
  useKibo();
  return null;
};

const AppLayout = () => (
  <>
    <KiboSync />
    <ConsentConfigurator />
    <Outlet />
  </>
);

const PlaygroundLayout = () => (
  <>
    <PlaygroundDataLoader />
    <Outlet />
  </>
);

const createAppRouter = () => {
  return createBrowserRouter([
    {
      element: <AppLayout />,
      HydrateFallback: () => null,
      children: [
        {
          path: '/auth/login',
          lazy: async () => {
            const { default: LoginRoute } = await import('./auth/Login.route');
            return { Component: LoginRoute };
          },
        },
        {
          path: '/auth/setup-account',
          lazy: async () => {
            const { default: SetupAccountRoute } = await import('./auth/SetupAccount.route');
            return { Component: SetupAccountRoute };
          },
        },
        {
          path: '/auth/resend-email-confirmation',
          lazy: async () => {
            const { default: ResendConfirmationEmailRoute } = await import('./auth/ResendConfirmationEmail.route');
            return { Component: ResendConfirmationEmailRoute };
          },
        },
        {
          path: '/auth/forgot-password',
          lazy: async () => {
            const { default: ForgotPasswordRoute } = await import('./auth/ForgotPassword.route');
            return { Component: ForgotPasswordRoute };
          },
        },
        {
          path: '/auth/reset-password',
          lazy: async () => {
            const { default: ResetPasswordRoute } = await import('./auth/ResetPassword.route');
            return { Component: ResetPasswordRoute };
          },
        },
        {
          path: '/oauth/authorize',
          lazy: async () => {
            const { default: OAuthAuthorizeRoute } = await import('./oauth/OAuthAuthorize.route');
            return { Component: OAuthAuthorizeRoute };
          },
        },
        {
          path: '/upgrade',
          lazy: async () => {
            const { default: UpgradeRedirectRoute } = await import('./upgrade/UpgradeRedirect.route');
            return { Component: UpgradeRedirectRoute };
          },
        },
        {
          path: '/appsumo',
          lazy: async () => {
            const { default: AppSumoRoute } = await import('./appsumo/AppSumo.route');
            return { Component: AppSumoRoute };
          },
        },
        {
          path: '/appsumo/confirm',
          lazy: async () => {
            const { default: AppSumoConfirmRoute } = await import('./appsumo/AppSumoConfirm.route');
            return { Component: AppSumoConfirmRoute };
          },
        },
        {
          element: <PlaygroundLayout />,
          children: [
            {
              path: '/analytics',
              lazy: async () => {
                const { default: AnalyticsRedirectRoute } = await import('./analytics/AnalyticsRedirect.route');
                return {
                  Component: () => (
                    <ProtectedRoute>
                      <AnalyticsRedirectRoute />
                    </ProtectedRoute>
                  ),
                };
              },
              errorElement: <RootErrorBoundary />,
            },
            {
              path: '/analytics/:username',
              lazy: async () => {
                const { default: AnalyticsRoute } = await import('./analytics/Analytics.route');
                return {
                  Component: () => (
                    <ProtectedRoute>
                      <CheckoutEmbedProvider>
                        <AnalyticsProvider>
                          <AnalyticsRoute />
                        </AnalyticsProvider>
                      </CheckoutEmbedProvider>
                    </ProtectedRoute>
                  ),
                };
              },
              errorElement: <RootErrorBoundary />,
            },
            {
              path: '/playground',
              lazy: async () => {
                const { default: PlaygroundRoute } = await import('./playground/Playground.route');
                return {
                  Component: () => (
                    <CheckoutEmbedProvider>
                      <AnalyticsProvider>
                        <PlaygroundRoute />
                      </AnalyticsProvider>
                    </CheckoutEmbedProvider>
                  ),
                };
              },
              errorElement: <RootErrorBoundary />,
            },
          ],
        },
        {
          path: '/:username',
          lazy: async () => {
            const { default: ShareablePreviewRoute } = await import('./page/ShareablePreview.route');
            return {
              Component: () => (
                <AnalyticsProvider>
                  <PlaygroundDataLoader />
                  <ShareablePreviewRoute />
                </AnalyticsProvider>
              ),
            };
          },
          errorElement: <RootErrorBoundary />,
        },
        {
          path: '*',
          lazy: async () => {
            if (window.location.pathname === '/') {
              if (window.location.host !== removeHttpProtocol(process.env.CLIENT_URL!)) {
                const { default: ShareablePreviewRoute } = await import('./page/ShareablePreview.route');
                return {
                  Component: () => (
                    <AnalyticsProvider>
                      <PlaygroundDataLoader />
                      <ShareablePreviewRoute />
                    </AnalyticsProvider>
                  ),
                };
              }
              window.location.href = `${process.env.CLIENT_URL!}/auth/login`;
              return { Component: () => null };
            }
            const { default: NotFoundRoute } = await import('./NotFound.route');
            return { Component: NotFoundRoute };
          },
          errorElement: <RootErrorBoundary />,
        },
      ],
    },
  ]);
};

export default createAppRouter;
